import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { BOLUMLER, SURUM, rotadanBolum, type Bolum } from './icerik';
import YardimSekmeleri from './YardimSekmeleri';

/**
 * KULLANIM KILAVUZU — ⓘ ile açılan okuma katmanı.
 *
 * Solda akış (bölüm listesi), sağda metin. `acilis` verilirse o bölümde
 * açılır — ⓘ düğmesi o anki ekranın bölümünü geçirir. `kapat` yoksa sayfa
 * kipindedir (/kilavuz, "Yeni sekmede aç" buraya gelir).
 *
 * Bölümler ROLE GÖRE süzülür: Satış rolü tahsilat bölümünü görmez, çünkü
 * ekranı da yok. Olmayan bir ekranı anlatmak kafa karıştırır.
 */
export default function Kilavuz({
  acilis, kapat, turBaslat,
}: { acilis?: string; kapat?: () => void; turBaslat?: (bolum: Bolum) => void }) {
  const { can } = useAuth();
  const { pathname, hash } = useLocation();

  const gorunen = useMemo(() => BOLUMLER.filter((b) => !b.cap || can(b.cap)), [can]);
  const ilk = acilis ?? hash.replace('#', '') ?? rotadanBolum(pathname, can)?.id;
  const [seciliId, setSeciliId] = useState(
    gorunen.some((b) => b.id === ilk) ? ilk! : gorunen[0]?.id
  );
  const secili = gorunen.find((b) => b.id === seciliId) ?? gorunen[0];

  useEffect(() => {
    if (!kapat) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && kapat();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [kapat]);

  /** `**kalın**` işaretini basitçe çevirir — markdown kütüphanesi istemiyoruz. */
  const vurgula = (metin: string) =>
    metin.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
      p.startsWith('**') ? <b key={i}>{p.slice(2, -2)}</b> : <span key={i}>{p}</span>
    );

  // Ekranlar arası tur (adımların rotası var) her yerden başlar; ekran turu
  // yalnız o ekrandayken — başka sayfadan düğmeleri gösteremez.
  const ekranlarArasi = secili?.adimlar?.some((a) => a.rota);
  const turuGosterebilir = turBaslat && secili?.turVar && (
    ekranlarArasi || (secili.rota && (secili.rota === '/' ? pathname === '/' : pathname.startsWith(secili.rota)))
  );

  const govde = (
    <div className="ccd-kilavuz-govde">
      <nav className="ccd-kilavuz-akis" aria-label="Kılavuz bölümleri">
        {gorunen.map((b) => (
          <button key={b.id} className={b.id === secili?.id ? 'ccd-is-active' : ''}
                  onClick={() => setSeciliId(b.id)}>
            <span className="ccd-kilavuz-no">{b.no}</span>
            <span>{b.baslik}</span>
          </button>
        ))}
      </nav>

      {secili && (
        <article className="ccd-kilavuz-metin" key={secili.id}>
          <div className="ccd-eyebrow">ART MOZAİK · PANEL · {secili.baslik.toLocaleUpperCase('tr')}</div>
          <h1 className="ccd-title" style={{ fontSize: 30, margin: '6px 0 8px' }}>{secili.baslik}</h1>
          <p className="ccd-meta" style={{ fontSize: 15 }}>{secili.ozet}</p>

          {turuGosterebilir && (
            <button className="ccd-btn ccd-btn-accent ccd-btn-sm" onClick={() => turBaslat!(secili)} style={{ margin: '6px 0 14px' }}>
              {ekranlarArasi ? '▶ Uçtan uca turu başlat (ekranlar arasında gezer)' : '▶ Bu ekranda adım adım göster'}
            </button>
          )}

          {secili.metin?.map((p, i) => <p key={i}>{vurgula(p)}</p>)}

          {secili.adimlar && secili.adimlar.length > 0 && (
            <>
              <h2 className="ccd-heading" style={{ marginTop: 22 }}>Adım adım</h2>
              {secili.adimlar.map((a, i) => (
                <div className="ccd-kilavuz-adim" key={i}>
                  <span className="ccd-kilavuz-adim-no">{i + 1}</span>
                  <div>
                    <div style={{ fontWeight: 600 }}>{a.baslik}</div>
                    <div className="ccd-meta" style={{ fontSize: 13.5 }}>{vurgula(a.metin)}</div>
                  </div>
                </div>
              ))}
            </>
          )}

          {secili.terimler && secili.terimler.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {/* Kelimelerin tam açıklaması, hesabı ve örneği fihristte. */}
              <p className="ccd-meta">
                Burada kısa tanımlar var. Her rakamın nasıl hesaplandığı, örneği ve hangi ekranda olduğu için{' '}
                <Link className="ccd-link" to="/fihrist" onClick={kapat}>Fihrist</Link>'e bakın; ekrandaki «?» işaretleri de oraya açılır.
              </p>
              {secili.terimler.map((t, i) => (
                <div className="ccd-kilavuz-senaryo" key={i}>
                  <b style={{ color: 'inherit' }}>{t.terim}</b>
                  <span>{vurgula(t.tanim)}</span>
                </div>
              ))}
            </div>
          )}

          {secili.senaryolar && secili.senaryolar.length > 0 && (
            <>
              <h2 className="ccd-heading" style={{ marginTop: 22 }}>Şu olursa</h2>
              {secili.senaryolar.map((s, i) => (
                <div className="ccd-kilavuz-senaryo" key={i}>
                  <b>{s.durum}</b>
                  <span>{vurgula(s.yap)}</span>
                </div>
              ))}
            </>
          )}

          <p className="ccd-meta" style={{ marginTop: 28 }}>{SURUM}</p>
        </article>
      )}
    </div>
  );

  // Sayfa kipi: kabuğun içinde, üst şerit yok.
  if (!kapat) {
    return (
      <>
        <YardimSekmeleri aktif="kilavuz" />
        <div className="ccd-card" style={{ padding: 0, overflow: 'hidden' }}>{govde}</div>
      </>
    );
  }

  return (
    <div className="ccd-kilavuz" role="dialog" aria-modal="true" aria-label="Kullanım kılavuzu">
      <div className="ccd-kilavuz-ust">
        <div>
          <b>Kullanım Kılavuzu</b>
          <span className="ccd-meta"> · okuyun, bitince kapatın</span>
        </div>
        <div className="ccd-row">
          <Link className="ccd-btn ccd-btn-ghost ccd-btn-sm" to="/fihrist" onClick={kapat}
                title="Her rakam ve kavram: ne nedir, nasıl hesaplanır">Fihrist</Link>
          <a className="ccd-btn ccd-btn-ghost ccd-btn-sm" href={`/kilavuz#${secili?.id ?? ''}`}
             target="_blank" rel="noreferrer">Yeni sekmede aç ↗</a>
          <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={kapat}>Kapat</button>
        </div>
      </div>
      {govde}
    </div>
  );
}
